import { afterNextRender, Injector } from '@angular/core';

/** Anything that can take the focus: a DOM element, or a component that forwards focus to one. */
export interface Focusable {
  focus(): void;
}

/**
 * Moves the focus to a target that the pending change detection cycle is about to render.
 *
 * The case this exists for: a handler changes some state, and the field it wants to focus is
 * rendered by that same state. At the moment the handler runs, the field's block is still on the
 * previous state - the `@ViewChild` is `undefined`, or the element is still `[hidden]` and therefore
 * not focusable. The focus call does nothing, without an error.
 *
 * `setTimeout(..., 0)` used to be late enough, because change detection ran synchronously at the end
 * of the zone task. The BackOffice bootstraps with `runCoalescing`, which defers change detection to
 * a `requestAnimationFrame` racing a timeout - both scheduled after a timeout registered from inside
 * the handler, so the focus call now runs first (#5711). `afterNextRender` runs once change
 * detection and rendering are through, whatever scheduled them, and it schedules a cycle itself.
 *
 * The target is passed as a getter rather than a value, because reading it now is exactly the
 * mistake: it has to be read inside the callback, after the view exists.
 */
export function focusWhenRendered(injector: Injector, getTarget: () => Focusable | null | undefined): void {
  afterNextRender(() => getTarget()?.focus(), { injector });
}
